import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import loreImg from '../assets/images/lore-img.png';
import ctaBgImg from '../assets/images/cta-bg.png';
import filterImg from '../assets/images/noise-filter.png';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useDominantColor } from '../hooks/useDominantColor';
import Navbar from '../components/Navbar';

/** Lighten RGB by mixing with white (0–1, e.g. 0.25 = 25% brighter) */
function lightenRgb(rgb: [number, number, number], amount: number): [number, number, number] {
  return rgb.map((c) => Math.round(c + (255 - c) * amount)) as [number, number, number];
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/** Convert RGB 0–255 to HSL (h 0–360, s 0–100, l 0–100) */
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      default: h = ((r - g) / d + 4) / 6;
    }
  }
  return [h * 360, s * 100, l * 100];
}

/** Convert HSL to RGB (0–255) */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h /= 360; s /= 100; l /= 100;
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1; if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

/** Make color more saturated and shift toward blue (hue ~210°) */
function toSaturatedBlue(rgb: [number, number, number]): [number, number, number] {
  const [h, s, l] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const blueHue = 210;
  const newH = h * 0.4 + blueHue * 0.6;
  const newS = Math.min(100, s * 1.5 + 35);
  const newL = Math.min(85, Math.max(45, l));
  return hslToRgb(newH, newS, newL);
}

const LorePage: React.FC = () => {
  const baseAccent = useDominantColor(loreImg);
  const lightened = lightenRgb(baseAccent.rgb, 0.2);
  const [r, g, b] = toSaturatedBlue(lightened);
  const accent = {
    hex: rgbToHex(r, g, b),
    rgb: [r, g, b] as [number, number, number],
    rgba: (a: number) => `rgba(${r},${g},${b},${a})`,
  };
  /* Brighter, image-matching color for heading (less blue than accent) */
  const [hr, hg, hb] = lightenRgb(baseAccent.rgb, 0.45);
  const headingStructureHex = rgbToHex(hr, hg, hb);
  const navigate = useNavigate();
  const glowRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef({ x: -300, y: -300 });
  const currentRef = useRef({ x: -300, y: -300 });
  const rafRef = useRef<number | null>(null);

  const onMouseMove = useCallback((e: MouseEvent) => {
    targetRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  useEffect(() => {
    const LERP = 0.07;
    const loop = () => {
      const cur = currentRef.current;
      const tgt = targetRef.current;
      cur.x += (tgt.x - cur.x) * LERP;
      cur.y += (tgt.y - cur.y) * LERP;
      if (glowRef.current) {
        glowRef.current.style.transform = `translate(${cur.x}px, ${cur.y}px)`;
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    window.addEventListener('mousemove', onMouseMove, { passive: true });

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [onMouseMove]);

  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.05 });
  const [heroRef, heroVisible] = useScrollAnimation({ threshold: 0.05 });
  const [section2Ref, section2Visible] = useScrollAnimation();
  const [section3Ref, section3Visible] = useScrollAnimation();
  const [section4Ref, section4Visible] = useScrollAnimation();
  const [section5Ref, section5Visible] = useScrollAnimation();
  const [section6Ref, section6Visible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation({ threshold: 0.1 });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const accentVars = {
    '--doc-accent': accent.hex,
    '--doc-accent-60': accent.rgba(0.6),
    '--doc-accent-40': accent.rgba(0.4),
    '--doc-accent-30': accent.rgba(0.3),
    '--doc-accent-20': accent.rgba(0.2),
    '--doc-accent-12': accent.rgba(0.12),
    '--doc-accent-05': accent.rgba(0.05),
    '--doc-accent-glow': `0 0 30px ${accent.rgba(0.5)}`,
    '--lore-numeral-color': headingStructureHex,
  } as React.CSSProperties;

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ backgroundColor: '#0C0D12', ...accentVars }}>
      <Navbar />

      <div
        ref={glowRef}
        className="doctrine-cursor-glow"
        style={{
          background: `radial-gradient(circle, ${accent.rgba(0.18)} 0%, ${accent.rgba(0.07)} 40%, transparent 70%)`,
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 lore-page">

        {/* Hero Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`doctrine-hero-header fade-in-up ${headerVisible ? 'animate' : ''}`}
        >
          <div className="doctrine-container">
            <h1 className="doctrine-hero-title">
              Seeing the <span style={{ color: headingStructureHex }}>Structure</span>
            </h1>
            <p className="doctrine-meta">
              Academy of Architects &nbsp;·&nbsp; 3 min read
            </p>
          </div>
        </div>

        {/* Hero Split: Image (left) + Text (right) */}
        <div
          ref={heroRef as React.RefObject<HTMLDivElement>}
          className={`doctrine-container lore-hero-split fade-in-up ${heroVisible ? 'animate' : ''}`}
        >
          <div className="doctrine-hero-image-wrap">
            <img
              src={loreImg}
              alt="Lore, the Architect's gaze"
              className="doctrine-hero-image"
            />
          </div>
          <div className="doctrine-hero-text">
            <div className="lore-section-label">
              <span className="lore-numeral-only">I</span>
            </div>
            <p className="doctrine-body-para">
              Most people believe effort is the deciding factor in life.
            </p>
            <p className="doctrine-body-para">
              Work harder. Try longer. Push through.
            </p>
            <p className="doctrine-body-para">
              And when results don&apos;t come, they turn inward. They assume the flaw is personal, a lack of discipline, intelligence, or worth.
            </p>
            <p className="doctrine-body-para">
              This belief is convenient. It keeps failure internal and the machinery of the world unseen.
            </p>
            <p className="doctrine-body-para">
              In truth, effort only produces results <strong>within a structure</strong>. Outside of it, effort dissipates, like force applied in empty space.
            </p>
            <p className="doctrine-body-para">
              The ancients sensed this, though they lacked the language. They spoke of fate, currents, and invisible hands. They were describing structure without knowing its shape.
            </p>
            <p className="doctrine-body-para">
              You can see it clearly once you stop moralizing outcomes. Two people work the same hours. One advances, the other stalls. Two people speak the same words. One is heard, the other ignored. Two people fail the same way. One recovers, the other collapses.
            </p>
            <p className="doctrine-body-para">
              The difference is rarely character. It is <strong>position</strong>.
            </p>
            <p className="doctrine-body-para">
              Most systems reward alignment, not intensity. They punish friction, not weakness. This is why advice so often fails. Advice assumes you are free to act. Most people are responding inside constraints they never agreed to. They move, but along rails.
            </p>
          </div>
        </div>

        {/* Article body */}
        <div className="doctrine-container doctrine-article-body">

          <div className="doctrine-divider" />

          {/* II & III side by side */}
          <div className="lore-sections-row">
            <div
              ref={section2Ref as React.RefObject<HTMLDivElement>}
              className={`doctrine-section lore-section-card fade-in-up ${section2Visible ? 'animate' : ''}`}
            >
              <div className="lore-section-label">
                <span className="lore-numeral-only">II</span>
              </div>
              <div className="doctrine-section-content">
                <p className="doctrine-body-para">
                  Structure hides by feeling ordinary. If chains were obvious, they would be resisted. So they present as routine. As culture. As &quot;the way things are.&quot; What repeats without question becomes invisible.
                </p>
                <p className="doctrine-body-para">
                  Consider attention. Where your attention rests determines what enters your mind, what grows there, and what withers unseen. Most people believe they choose this. They don&apos;t. Attention is pulled, by urgency, by novelty, by reward. Patterns that persist because they function efficiently.
                </p>
                <p className="doctrine-body-para">
                  Once repeated long enough, influence stops feeling external. It settles into habit. Then into identity. This is how systems conceal themselves. They do not announce control. They <strong>normalize participation</strong>.
                </p>
              </div>
            </div>

            <div
              ref={section3Ref as React.RefObject<HTMLDivElement>}
              className={`doctrine-section lore-section-card fade-in-up ${section3Visible ? 'animate' : ''}`}
            >
              <div className="lore-section-label">
                <span className="lore-numeral-only">III</span>
              </div>
              <div className="doctrine-section-content">
                <p className="doctrine-body-para">
                  The First Realization does not arrive dramatically. There is no sign. No voice. No revelation. It appears as a subtle fracture in interpretation.
                </p>
                <p className="doctrine-body-para">
                  You notice that effort and outcome are not honestly linked. That the same patterns recur in different settings. That new environments reproduce old limits. At first, this feels like personal failure. Later, it feels like recognition.
                </p>
                <p className="doctrine-body-para">
                  This is the moment where sleep ends, not because you &quot;wake up,&quot; but because you stop explaining everything through yourself. You begin to sense that the world is not hostile. It is <strong>arranged</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* IV & V side by side */}
          <div className="lore-sections-row">
            <div
              ref={section4Ref as React.RefObject<HTMLDivElement>}
              className={`doctrine-section lore-section-card fade-in-up ${section4Visible ? 'animate' : ''}`}
            >
              <div className="lore-section-label">
                <span className="lore-numeral-only">IV</span>
              </div>
              <div className="doctrine-section-content">
                <p className="doctrine-body-para">
                  Structure is not evil. It is not benevolent. It simply exists. Those who never see it live inside it unconsciously, reacting to pressure they cannot name. Those who glimpse it gain distance, not power yet, but <strong>orientation</strong>.
                </p>
                <p className="doctrine-body-para">
                  They understand something quietly dangerous: What exists above shapes what unfolds below. What accumulates below feeds back and reshapes what exists above. Influence is circular. Delayed. Often invisible until it is complete.
                </p>
                <p className="doctrine-body-para">
                  This is why surface action so often fails. It treats effects as causes. The Architect does not worship this knowledge. They do not rebel against it. They study it.
                </p>
              </div>
            </div>

            <div
              ref={section5Ref as React.RefObject<HTMLDivElement>}
              className={`doctrine-section lore-section-card fade-in-up ${section5Visible ? 'animate' : ''}`}
            >
              <div className="lore-section-label">
                <span className="lore-numeral-only">V</span>
              </div>
              <div className="doctrine-section-content">
                <p className="doctrine-body-para">
                  This volume offers no techniques. No rituals. No instructions. That is intentional.
                </p>
                <p className="doctrine-body-para">
                  To act without understanding structure is to strengthen the very patterns you wish to escape. Before movement, there must be orientation. Before intervention, there must be sight.
                </p>
                <p className="doctrine-body-para">
                  Most people rush forward blindly. They call it ambition. The Architect pauses. Not out of fear, but because clarity precedes precision.
                </p>
              </div>
            </div>
          </div>

          {/* VI full width */}
          <div
            ref={section6Ref as React.RefObject<HTMLDivElement>}
            className={`doctrine-section fade-in-up ${section6Visible ? 'animate' : ''}`}
          >
            <div className="lore-section-label">
              <span className="lore-numeral-only">VI</span>
            </div>
            <div className="doctrine-section-content">
              <p className="doctrine-body-para">
                The First Realization is not power. It is permission. Permission to stop interpreting your life as a moral story and begin reading it as a constructed system.
              </p>
              <p className="doctrine-body-para">
                The world has not changed. But it has become <strong>legible</strong>. And anything legible can, eventually, be redesigned.
              </p>
            </div>
          </div>

          <div className="doctrine-divider" />
        </div>

        {/* CTA Section */}
        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className={`relative min-h-[65vh] flex items-center justify-center overflow-hidden px-4 sm:px-6 lg:px-8 py-16 sm:py-20 fade-in-scale ${ctaVisible ? 'animate' : ''}`}
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${ctaBgImg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'blur(4px)',
              WebkitFilter: 'blur(4px)'
            }}
          />
          <div
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              backgroundImage: `url(${filterImg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              mixBlendMode: 'overlay',
              opacity: 0.6
            }}
          />
          <div
            className="absolute top-0 left-0 right-0 h-32 sm:h-40 lg:h-48 pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom, rgba(12,13,18,1) 0%, rgba(12,13,18,0.8) 30%, rgba(12,13,18,0.4) 70%, transparent 100%)',
              zIndex: 1
            }}
          />
          <div
            className="absolute bottom-0 left-0 right-0 h-32 sm:h-40 lg:h-48 pointer-events-none"
            style={{
              background: 'linear-gradient(to top, rgba(12,13,18,1) 0%, rgba(12,13,18,0.8) 30%, rgba(12,13,18,0.4) 70%, transparent 100%)',
              zIndex: 1
            }}
          />
          <div className="absolute inset-0 bg-black/40" />

          <div className="relative z-10 max-w-4xl mx-auto text-center cta-container">
            <div className="space-y-4 sm:space-y-6">
              <div className="space-y-1 sm:space-y-2">
                <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-amber-100 leading-relaxed" style={{ letterSpacing: '-0.01em' }}>
                  Most people react to outcomes.
                </p>
                <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-amber-100 leading-relaxed" style={{ letterSpacing: '-0.01em' }}>
                  We design the causes behind them.
                </p>
              </div>
              <p className="cta-text text-xl font-normal sm:text-2xl md:text-3xl text-amber-50/90 leading-relaxed">
                Some doors open only from the inside.
              </p>
            </div>
            <div className="mt-10 sm:mt-14">
              <div className="w-32 h-px bg-gradient-to-r from-transparent via-amber-300/60 to-transparent mx-auto" />
            </div>
            <div className="mt-6 sm:mt-8">
              <button
                className="cta-button px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base tracking-wider font-mono"
                onClick={() => navigate('/#pricing')}
              >
                Join Inner Circle
              </button>
            </div>
          </div>
        </div>

        <footer className="relative z-10 py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12 px-4 sm:px-6 lg:px-8">
            <div className="text-center lg:text-left lg:col-span-2">
              <h3 className="jacquard-font text-3xl sm:text-4xl mb-4">Order of Architects</h3>
              <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-4 max-w-md lg:max-w-lg mx-auto lg:mx-0">
                We are Architects of causality, seeing the invisible chains of reality and placing new ones by choice.
              </p>
            </div>
            <div className="text-center lg:text-left">
              <h4 className="text-white font-semibold mb-4 text-lg">Resources</h4>
              <ul className="space-y-2 text-sm sm:text-base">
                <li><a href="/doctrine" className="text-gray-400 hover:text-white transition-colors duration-300">Doctrine</a></li>
                <li><a href="/lore" className="text-gray-400 hover:text-white transition-colors duration-300">Lore</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Ritual</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Archives</a></li>
              </ul>
            </div>
            <div className="text-center lg:text-left">
              <h4 className="text-white font-semibold mb-4 text-lg">Join the Community</h4>
              <p className="text-gray-400 text-sm sm:text-base mb-4">
                Connect with fellow Architects in our Discord server.
              </p>
              <a href="#" className="discord-button inline-flex items-center space-x-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.019 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z"/>
                </svg>
                <span>Join Discord</span>
              </a>
            </div>
          </div>
          <div className="pt-8 sm:pt-12 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
              <p className="text-gray-300 text-sm">© 2026 Order of Architects. All rights reserved.</p>
              <div className="flex items-center gap-4 sm:gap-6 text-sm">
                <a href="#" className="text-gray-300 hover:text-white transition-colors duration-300">Privacy Policy</a>
                <a href="#" className="text-gray-300 hover:text-white transition-colors duration-300">Terms of Service</a>
              </div>
            </div>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default LorePage;
